import Link from "next/link";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Feature_collections_data from "../../data/Feature_collections_data";
import Image from "next/image";

const Explore_collection_item = ({
  itemFor,
  searchQuery = "",
  allData,
  viewMode = "grid",
}) => {
  const { sortedCollectionData } = useSelector((state) => state.counter);

  const [itemData, setItemData] = useState([]);
  const [localCampaigns, setCampaigns] = useState([]);

  useEffect(() => {
    if (itemFor === "userPage") {
      setItemData(Feature_collections_data.slice(0, 4));
    } else {
      setItemData(sortedCollectionData);
    }
  }, [sortedCollectionData, itemFor]);

  useEffect(() => {
    if (!allData?.allCampaigns) return;

    // Filter campaigns based on search query
    let filteredCampaigns = allData.allCampaigns;

    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredCampaigns = allData.allCampaigns.filter((campaign) => {
        const name = campaign.overview?.campaignName?.toLowerCase() || "";
        const description = campaign.overview?.description?.toLowerCase() || "";
        return name.includes(query) || description.includes(query);
      });
    }

    setCampaigns([{
      count: allData?.count || {},
      campaigns: filteredCampaigns,
    }]);
  }, [searchQuery, allData]);

  // Helper function to get campaign status
  const getCampaignStatus = (campaign) => {
    const now = new Date();
    const endDate = new Date(campaign.overview.endDate);
    const startDate = new Date(campaign.overview.startDate);

    if (now < startDate) {
      return {
        text: "Upcoming",
        color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      };
    } else if (now > endDate) {
      return {
        text: "Ended",
        color: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
      };
    } else {
      return {
        text: "Active",
        color:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      };
    }
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + "...";
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      {localCampaigns.map((item) => {
        const { campaigns } = item;

        return campaigns.map((campaignItem, campaignIndex) => {
          const imageName = campaignItem.overview.campaignName;
          const campaignId = campaignItem._id;
          const stringWithoutSpaces = imageName.replace(/\s/g, "");

          // Use actual banner image URL if available, otherwise fall back to placeholder
          const campaignImageTemp = (() => {
            if (
              campaignItem.overview.bannerImageUrl &&
              campaignItem.overview.bannerImageUrl.length > 0
            ) {
              const imageUrl = campaignItem.overview.bannerImageUrl[0];

              // If it's already an absolute URL (starts with http), use as is
              if (
                imageUrl.startsWith("http://") ||
                imageUrl.startsWith("https://")
              ) {
                return imageUrl;
              }

              // If it's a relative path without leading slash, add backend URL
              if (!imageUrl.startsWith("/")) {
                return `${process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000"}/${imageUrl}`;
              }

              // If it starts with slash but isn't absolute, add backend URL
              return `${process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000"}${imageUrl}`;
            }

            // Fallback to placeholder
            return (
              "/images/collections/campaigns/" + stringWithoutSpaces + ".png"
            );
          })();

          const status = getCampaignStatus(campaignItem);

          // Grid View (Clean Card Design)
          if (viewMode === "grid") {
            return (
              <article key={campaignIndex} className="h-full">
                <div className="bg-white dark:bg-jacarta-700 border border-jacarta-100 dark:border-jacarta-600 rounded-2xl overflow-hidden transition-shadow duration-200 hover:shadow-md h-full flex flex-col">
                  {/* Campaign Image */}
                  <Link href={`/campaign/${campaignId}`} className="block">
                    <div className="relative w-full h-40 overflow-hidden">
                      <Image
                        width={300}
                        height={200}
                        src={campaignImageTemp}
                        alt={campaignItem.overview.campaignName}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}
                        >
                          {status.text}
                        </span>
                      </div>
                      {/* Super Campaign Badge */}
                      {campaignItem.overview.isSuperCampaign && (
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            Super Campaign
                          </span>
                        </div>
                      )}
                      {/* Sub Campaign Badge */}
                      {campaignItem.overview.isSubCampaign &&
                        !campaignItem.overview.isSuperCampaign &&
                        campaignItem.overview.superCampaignName && (
                          <div className="absolute top-3 left-3">
                            <span className="inline-block max-w-[160px] truncate px-2 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-700 border border-purple-300 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-700">
                              Part of {campaignItem.overview.superCampaignName}
                            </span>
                          </div>
                        )}
                    </div>
                  </Link>

                  {/* Campaign Content */}
                  <div className="p-4 flex-1 flex flex-col">
                    {/* Campaign Title */}
                    <Link
                      href={`/campaign/${campaignId}`}
                      className="font-display text-base font-semibold text-jacarta-700 dark:text-white hover:text-accent dark:hover:text-accent transition-colors line-clamp-2 mb-2"
                    >
                      {campaignItem.overview.campaignName}
                    </Link>

                    {/* Campaign Description */}
                    <p className="text-sm text-jacarta-500 dark:text-jacarta-400 mb-3 line-clamp-2 flex-1">
                      {truncateText(campaignItem.overview.description, 80)}
                    </p>

                    {/* Creator Info */}
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-jacarta-100 dark:border-jacarta-600 mt-auto">
                      <div className="flex items-center gap-2">
                        <span className="text-jacarta-400 dark:text-jacarta-500">
                          by
                        </span>
                        <span className="text-jacarta-700 dark:text-jacarta-300 font-medium truncate">
                          {campaignItem.creatorInfo?.displayName ||
                            "Unknown Creator"}
                        </span>
                      </div>
                      {campaignItem.overview.country && (
                        <div className="flex items-center gap-1 text-jacarta-400 dark:text-jacarta-500">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-xs truncate">
                            {campaignItem.overview.country}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
          }

          // List View (New Clean Design)
          return (
            <article key={campaignIndex} className="mb-4">
              <div className="bg-white dark:bg-jacarta-700 border border-jacarta-100 dark:border-jacarta-600 rounded-xl transition-shadow duration-200 hover:shadow-md">
                <div className="flex items-center p-4 md:p-6">
                  {/* Campaign Image */}
                  <div className="flex-shrink-0 mr-4 md:mr-6">
                    <Link href={`/campaign/${campaignId}`}>
                      <Image
                        width={80}
                        height={80}
                        src={campaignImageTemp}
                        alt={campaignItem.overview.campaignName}
                        className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover"
                        loading="lazy"
                      />
                    </Link>
                  </div>

                  {/* Campaign Content */}
                  <div className="flex-grow min-w-0">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-4">
                      {/* Left Content */}
                      <div className="flex-grow min-w-0">
                        {/* Campaign Name & Status */}
                        <div className="flex items-center gap-3 mb-2">
                          <Link
                            href={`/campaign/${campaignId}`}
                            className="font-display text-lg font-semibold text-jacarta-700 dark:text-white hover:text-accent dark:hover:text-accent transition-colors truncate"
                          >
                            {campaignItem.overview.campaignName}
                          </Link>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}
                          >
                            {status.text}
                          </span>
                          {campaignItem.overview.isSuperCampaign && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              Super Campaign
                            </span>
                          )}
                          {campaignItem.overview.isSubCampaign &&
                            !campaignItem.overview.isSuperCampaign &&
                            campaignItem.overview.superCampaignName && (
                              <span className="inline-block max-w-[180px] truncate px-2 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-700 border border-purple-300 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-700">
                                Part of {campaignItem.overview.superCampaignName}
                              </span>
                            )}
                        </div>

                        {/* Description */}
                        <p className="text-sm text-jacarta-500 dark:text-jacarta-400 mb-3 leading-relaxed">
                          {truncateText(campaignItem.overview.description, 150)}
                        </p>

                        {/* Meta Information */}
                        <div className="flex flex-wrap items-center gap-4 text-xs text-jacarta-400 dark:text-jacarta-500">
                          <div className="flex items-center gap-1">
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>
                              Ends {formatDate(campaignItem.overview.endDate)}
                            </span>
                          </div>
                          {campaignItem.impact.category && (
                            <div className="flex items-center gap-1">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span>{campaignItem.impact.category}</span>
                            </div>
                          )}
                          {campaignItem.overview.country && (
                            <div className="flex items-center gap-1">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span>{campaignItem.overview.country}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Content - Organization & Action */}
                      <div className="flex-shrink-0 flex flex-col items-end gap-3">
                        {/* Creator */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-jacarta-400 dark:text-jacarta-500">
                            by
                          </span>
                          <span className="text-sm text-jacarta-700 dark:text-jacarta-300 font-medium">
                            {campaignItem.creatorInfo?.displayName ||
                              "Unknown Creator"}
                          </span>
                        </div>

                        {/* Action Button */}
                        <Link
                          href={`/campaign/${campaignId}`}
                          className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark transition-colors"
                        >
                          View Campaign
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          );
        });
      })}
    </>
  );
};

export default Explore_collection_item;
